import { useState, useCallback, useMemo } from 'react';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { BookMarked, Calendar, Library, Paperclip, Users } from 'lucide-react';
import apiClient from '@/api/client';
import { QUERY_KEYS } from '@/api/queryKeys';
import { useConfirm } from '@/components/common/ConfirmDialog';
import { useAuthStore } from '@/store/useAuthStore';
import { FactCitationsSection } from './FactCitationsSection';
import type { PersonDetail } from '@/api/persons.api';

// ─── Typen ──────────────────────────────────────────────────────────────────

interface Source {
  id: string;
  title: string | null;
  author: string | null;
  publication?: string | null;
  /** Datum der Quelle selbst (ISO oder null). */
  date?: string | null;
  url: string | null;
  notes?: string | null;
  /** Wie viele Personen diese Quelle belegt (1 = nur diese). */
  linkedPersonCount?: number;
  /** Verknüpftes Dokument im Archiv. */
  media?: { id: string; filename: string; originalName: string | null; mimeType: string | null } | null;
}

interface CreateSourceDto {
  title?: string;
  author?: string;
  publication?: string;
  date?: string;
  url?: string;
  notes?: string;
  mediaId?: string | null;
}

/** Bereits im Baum erfasste Quelle – dient als Vorlage für neue Einträge. */
interface SourceTemplate {
  id: string;
  title: string | null;
  author: string | null;
  publication: string | null;
  date: string | null;
  url: string | null;
  notes: string | null;
  usageCount: number;
}

/** Datumsanzeige: ISO → TT.MM.JJJJ */
function formatSourceDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('de-DE');
}

const INPUT_CLASS =
  'w-full rounded border border-slate-300 bg-white px-2.5 py-2 text-sm text-slate-900 ' +
  'placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400';

// ─── Inline-Formular ────────────────────────────────────────────────────────

function SourceForm({
  personId,
  source,
  onDone,
}: {
  personId: string;
  /** Vorhandene Quelle → Bearbeiten (PUT); ohne → Neu anlegen (POST). */
  source?: Source;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const isEdit = !!source;
  const [title, setTitle] = useState(source?.title ?? '');
  const [author, setAuthor] = useState(source?.author ?? '');
  const [publication, setPublication] = useState(source?.publication ?? '');
  const [date, setDate] = useState(source?.date ? source.date.slice(0, 10) : '');
  const [url, setUrl] = useState(source?.url ?? '');
  const [notes, setNotes] = useState(source?.notes ?? '');
  const [mediaId, setMediaId] = useState(source?.media?.id ?? '');

  // Dateien dieser Person – als Beleg-Dokument anhängbar (Punkt 3).
  const { data: personMedia } = useQuery<Array<{ id: string; originalName: string | null; filename: string }>>({
    queryKey: ['media', personId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/persons/${personId}/media`);
      return data;
    },
  });

  // ─── Vorlagen: bereits im Baum erfasste Quellen ──────────────────────────
  // Dieselbe Kirchenbuchseite belegt oft mehrere Personen – hier lässt sie
  // sich mit einem Klick übernehmen, statt sie neu zu tippen.
  const [showTemplates, setShowTemplates] = useState(false);
  const [templateQuery, setTemplateQuery] = useState('');

  const { data: templates, isFetching: templatesLoading } = useQuery<{ data: SourceTemplate[] }>({
    queryKey: ['source-catalog', templateQuery],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: SourceTemplate[] }>('/sources/catalog', {
        params: templateQuery.trim().length >= 2 ? { q: templateQuery.trim() } : {},
      });
      return data;
    },
    enabled: showTemplates,
    placeholderData: (prev) => prev,
  });

  const templateList = useMemo(() => templates?.data ?? [], [templates]);

  // Vorhandene Quelle MITBENUTZEN statt kopieren: derselbe Datensatz belegt
  // dann mehrere Personen, spätere Korrekturen wirken überall.
  const linkExisting = useMutation({
    mutationFn: async (sourceId: string) => {
      await apiClient.post(`/persons/${personId}/sources/link`, { sourceId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.persons.detail(personId) });
      toast.success('Quelle verknüpft.');
      onDone();
    },
    onError: () => toast.error('Quelle konnte nicht verknüpft werden.'),
  });

  const saveSource = useMutation({
    mutationFn: async (dto: CreateSourceDto) => {
      if (isEdit) {
        await apiClient.put(`/persons/${personId}/sources/${source!.id}`, dto);
      } else {
        await apiClient.post(`/persons/${personId}/sources`, dto);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.persons.detail(personId) });
      toast.success(isEdit ? 'Quelle aktualisiert.' : 'Quelle hinzugefügt.');
      onDone();
    },
    onError: () => {
      toast.error(isEdit ? 'Quelle konnte nicht aktualisiert werden.' : 'Quelle konnte nicht erstellt werden.');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('Titel ist erforderlich.');
      return;
    }
    saveSource.mutate({
      title: title.trim(),
      author: author.trim() || undefined,
      publication: publication.trim() || undefined,
      date: date || undefined,
      url: url.trim() || undefined,
      notes: notes.trim() || undefined,
      mediaId: mediaId || null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3 rounded-lg border border-blue-200 bg-blue-50/30 p-3">
      {/* Vorlage übernehmen – nur beim Neuanlegen sinnvoll */}
      {!isEdit && (
        <div>
          <button
            type="button"
            onClick={() => setShowTemplates((v) => !v)}
            className={`flex w-full items-center justify-center gap-1.5 rounded border px-2.5 py-2 text-xs font-medium transition-colors ${
              showTemplates
                ? 'border-blue-300 bg-blue-100 text-blue-700'
                : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50'
            }`}
            aria-expanded={showTemplates}
          >
            <Library className="h-3.5 w-3.5" aria-hidden="true" />
            Vorhandene Quelle verknüpfen
          </button>

          {showTemplates && (
            <div className="mt-2 rounded border border-slate-200 bg-white p-2">
              <input
                type="text"
                value={templateQuery}
                onChange={(e) => setTemplateQuery(e.target.value)}
                placeholder="Quelle suchen…"
                className={INPUT_CLASS}
                aria-label="Vorhandene Quellen durchsuchen"
              />
              <p className="mt-2 text-[11px] leading-snug text-slate-500">
                Die Quelle wird mitbenutzt, nicht kopiert – spätere Korrekturen
                wirken bei allen belegten Personen.
              </p>
              <div className="mt-2 max-h-52 space-y-1 overflow-y-auto">
                {templatesLoading && templateList.length === 0 && (
                  <p className="py-3 text-center text-xs text-slate-400">Wird geladen…</p>
                )}
                {!templatesLoading && templateList.length === 0 && (
                  <p className="py-3 text-center text-xs text-slate-400">
                    Noch keine Quellen im Stammbaum.
                  </p>
                )}
                {templateList.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => linkExisting.mutate(t.id)}
                    disabled={linkExisting.isPending}
                    className="w-full rounded border border-slate-200 px-2.5 py-2 text-left hover:border-blue-300 hover:bg-blue-50"
                  >
                    <span className="block truncate text-sm font-medium text-slate-800">
                      {t.title ?? 'Ohne Titel'}
                    </span>
                    <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-slate-500">
                      {t.author && <span className="truncate">{t.author}</span>}
                      {t.publication && <span className="truncate">{t.publication}</span>}
                      {t.usageCount > 1 && (
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-500">
                          {t.usageCount}× genutzt
                        </span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div>
        <label htmlFor="src-title" className="mb-1 block text-xs font-medium text-slate-600">
          Titel *
        </label>
        <input
          id="src-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="z. B. Kirchenbuch St. Marien, Taufregister"
          className={INPUT_CLASS}
          autoFocus
        />
      </div>

      <div>
        <label htmlFor="src-author" className="mb-1 block text-xs font-medium text-slate-600">
          Autor / Urheber
        </label>
        <input
          id="src-author"
          type="text"
          value={author}
          onChange={(e) => setAuthor(e.target.value)}
          placeholder="z. B. Standesamt Mitte"
          className={INPUT_CLASS}
        />
      </div>

      <div>
        <label htmlFor="src-publication" className="mb-1 block text-xs font-medium text-slate-600">
          Fundstelle / Signatur
        </label>
        <input
          id="src-publication"
          type="text"
          value={publication}
          onChange={(e) => setPublication(e.target.value)}
          placeholder="z. B. Landesarchiv Berlin, Bd. 12, S. 34"
          className={INPUT_CLASS}
        />
      </div>

      <div>
        <label htmlFor="src-date" className="mb-1 block text-xs font-medium text-slate-600">
          Datum der Quelle
        </label>
        <input
          id="src-date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className={INPUT_CLASS}
        />
      </div>

      <div>
        <label htmlFor="src-url" className="mb-1 block text-xs font-medium text-slate-600">
          URL
        </label>
        <input
          id="src-url"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://…"
          className={INPUT_CLASS}
        />
      </div>

      <div>
        <label htmlFor="src-media" className="mb-1 block text-xs font-medium text-slate-600">
          Beleg-Datei aus dem Archiv
        </label>
        <select
          id="src-media"
          value={mediaId}
          onChange={(e) => setMediaId(e.target.value)}
          className={INPUT_CLASS}
        >
          <option value="">— keine —</option>
          {(personMedia ?? []).map((m) => (
            <option key={m.id} value={m.id}>
              {m.originalName ?? m.filename}
            </option>
          ))}
        </select>
        {(personMedia ?? []).length === 0 && (
          <p className="mt-1 text-[11px] text-slate-400">
            Für diese Person liegen noch keine Dateien im Archiv.
          </p>
        )}
      </div>

      <div>
        <label htmlFor="src-notes" className="mb-1 block text-xs font-medium text-slate-600">
          Notizen zu dieser Person
        </label>
        <textarea
          id="src-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="z. B. Eintrag Nr. 7, Randvermerk"
          rows={2}
          className={INPUT_CLASS}
        />
      </div>

      <div className="flex gap-2 pt-0.5">
        <button
          type="submit"
          disabled={saveSource.isPending}
          className="flex-1 rounded bg-blue-600 px-3 py-2 text-xs font-medium text-white shadow-sm
                     hover:bg-blue-700 disabled:opacity-50 sm:flex-none"
        >
          {saveSource.isPending ? 'Speichern...' : 'Speichern'}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="rounded px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100"
        >
          Abbrechen
        </button>
      </div>
    </form>
  );
}

// ─── SourcesTab ─────────────────────────────────────────────────────────────

interface SourcesTabProps {
  person: PersonDetail;
}

export function SourcesTab({ person }: SourcesTabProps) {
  const canManage = useAuthStore((s) => s.hasPermission('persons.manage'));
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const deleteSource = useMutation({
    mutationFn: async (sourceId: string) => {
      await apiClient.delete(`/persons/${person.id}/sources/${sourceId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.persons.detail(person.id) });
      toast.success('Quelle entfernt.');
    },
    onError: () => {
      toast.error('Quelle konnte nicht gelöscht werden.');
    },
  });

  const handleDelete = useCallback(
    async (sourceId: string, title: string | null, sharedWith: number) => {
      // Belegt die Quelle mehrere Personen, wird hier nur die Verknüpfung zu
      // DIESER Person gelöst – der Datensatz bleibt für die anderen bestehen.
      const shared = sharedWith > 1;
      const ok = await confirm({
        title: shared ? 'Quelle von dieser Person lösen?' : 'Quelle löschen?',
        message: shared ? (
          <>
            <strong>"{title ?? 'Ohne Titel'}"</strong> belegt {sharedWith} Personen. Die
            Verknüpfung zu dieser Person wird gelöst; bei den übrigen bleibt die Quelle
            erhalten.
          </>
        ) : (
          <>
            Quelle <strong>"{title ?? 'Ohne Titel'}"</strong> wirklich löschen?
          </>
        ),
        confirmLabel: shared ? 'Lösen' : 'Löschen',
        variant: 'danger',
      });
      if (!ok) return;
      deleteSource.mutate(sourceId);
    },
    [confirm, deleteSource],
  );

  const sources: Source[] = person.sources ?? [];

  return (
    <div className="space-y-4">
      {/* Hinzufügen-Button (nur mit persons.manage) */}
      {canManage && (
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-slate-300
                     py-3 text-sm font-medium text-slate-500 transition-colors hover:border-blue-400 hover:text-blue-600"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          Quelle hinzufügen
        </button>
      )}

      {/* Inline-Formular (Neu anlegen) */}
      {showForm && (
        <SourceForm personId={person.id} onDone={() => setShowForm(false)} />
      )}

      {/* Leerer Zustand */}
      {sources.length === 0 && !showForm && (
        <p className="py-4 text-center text-sm text-slate-400">
          Noch keine Quellen vorhanden.
        </p>
      )}

      {/* Quellen-Liste */}
      {sources.length > 0 && (
        <div className="space-y-2">
          {sources.map((source) => (
            <div
              key={source.id}
              className="group rounded-lg border border-slate-200 bg-slate-50/50 p-3"
            >
              {editingId === source.id ? (
                <SourceForm personId={person.id} source={source} onDone={() => setEditingId(null)} />
              ) : (
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  {/* Titel */}
                  <h5 className="text-sm font-medium text-slate-800">
                    {source.title ?? 'Ohne Titel'}
                  </h5>

                  {/* Autor */}
                  {source.author && (
                    <p className="mt-0.5 text-xs text-slate-500">von {source.author}</p>
                  )}

                  {/* Fundstelle und Datum der Quelle */}
                  {(source.publication || formatSourceDate(source.date)) && (
                    <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500">
                      {source.publication && (
                        <span className="inline-flex items-center gap-1">
                          <BookMarked className="h-3 w-3 shrink-0 text-slate-400" aria-hidden="true" />
                          {source.publication}
                        </span>
                      )}
                      {formatSourceDate(source.date) && (
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3 w-3 shrink-0 text-slate-400" aria-hidden="true" />
                          {formatSourceDate(source.date)}
                        </span>
                      )}
                    </p>
                  )}

                  {/* URL */}
                  {source.url && (
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 block truncate text-xs text-blue-600 underline hover:text-blue-800"
                    >
                      {source.url}
                    </a>
                  )}

                  {/* Verknüpfte Archiv-Datei */}
                  {source.media && (
                    <a
                      href={`/uploads/${source.media.filename}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 inline-flex max-w-full items-center gap-1 text-xs text-blue-600 underline hover:text-blue-800"
                    >
                      <Paperclip className="h-3 w-3 shrink-0" aria-hidden="true" />
                      <span className="truncate">
                        {source.media.originalName ?? source.media.filename}
                      </span>
                    </a>
                  )}

                  {/* Notizen */}
                  {source.notes && (
                    <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{source.notes}</p>
                  )}

                  {/* Beleg für mehrere Personen – Änderungen wirken überall */}
                  {(source.linkedPersonCount ?? 1) > 1 && (
                    <p className="mt-1.5 inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-500">
                      <Users className="h-3 w-3" aria-hidden="true" />
                      belegt {source.linkedPersonCount} Personen
                    </p>
                  )}
                </div>

                {/* Bearbeiten/Löschen (nur mit persons.manage). Auf Touch-Geräten
                    immer sichtbar – dort gibt es kein Hover. */}
                {canManage && (
                  <div className="flex shrink-0 items-center gap-0.5 transition-opacity lg:opacity-0 lg:group-hover:opacity-100 lg:focus-within:opacity-100">
                    <button
                      onClick={() => setEditingId(source.id)}
                      className="rounded p-1 text-slate-300 hover:bg-blue-50 hover:text-blue-600"
                      aria-label="Quelle bearbeiten"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDelete(source.id, source.title, source.linkedPersonCount ?? 1)}
                      className="rounded p-1 text-slate-300 hover:bg-red-50 hover:text-red-500"
                      aria-label="Quelle entfernen"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 00-7.5 0" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Belege je einzelner Angabe (Geburtsdatum, Ort …) */}
      <FactCitationsSection person={person} />
    </div>
  );
}
